import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@material-ui/core/Button";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import StateContext from "../../StateContext";
import { get_lang } from "../../Functions/getLang";
import { localizeWesternDigitsForLang } from "../../Functions/localizeNumerals";
import { formatMoney, normalizeCurrencyCode } from "../../helper/customeObj";
import InvoiceSharedHeader from "../../Components/invoiceShared/InvoiceSharedHeader";
import InvoiceSharedFooter from "../../Components/invoiceShared/InvoiceSharedFooter";
import { InvoiceStatusIconPng, InvoiceStatusIconSvg } from "./InvoiceStatusIcon";
import "./InvoiceDesign.scss";
import StandardBtn from "../../Components/standardBtn/StandardBtn";
import { getLiveData } from "../../Components/Pusher";
import DispatchContext from "../../DispatchContext";
import AddsSlider from "../../Components/addsBanners/addsSlider";
import ReviewModalv2 from "../../Components/reviewModalv2/reviewModalv2";
import vendorsConfig from "../../vendorsConfig";
import PartialPaymentSummary from "../../Components/partialPaymentSummary/PartialPaymentSummary";

const defaultDetails = [
  { label: "Invoice", value: "#2919672" },
  { label: "Payment", value: "Fat Cap" },
  { label: "Location", value: "Kuwait" },
  { label: "Table", value: "1" },
  { label: "Date", value: "Today" },
];

const defaultOrderItems = [
  { id: "d1", kind: "product", name: "Spanish Latte", quantityText: "1 item", price: "2.250 د.ك" },
  { id: "d2", kind: "product", name: "Croissant", quantityText: "1 item", price: "2.900 د.ك" },
  { id: "d3", kind: "product", name: "Iced Matcha", quantityText: "1 item", price: "2.000 د.ك" },
];

function addonLineName(addon, lang) {
  const raw = addon?.product_addon?.addon?.name;
  if (raw?.en) return raw.en;
  return get_lang(raw, lang) || "-";
}

/** Treats "-", "#-", empty, null/undefined as missing so the row can be hidden. */
function receiptDetailValuePresent(value) {
  if (value == null) return false;
  const s = String(value).trim();
  if (s === "" || s === "-") return false;
  if (s === "#-") return false;
  return true;
}

function receiptDetailRow(label, value) {
  return { label, value, show: receiptDetailValuePresent(value) };
}

function InvoiceDesign({
  storeInfo,
  invoiceDetail,
  eventOrder = false,
  amount,
  handleTryAgain,
  languageLabel = "عربي",
  onLanguageToggle,
  onLoginClick,
  onShareClick,
  paidTitle = "Paid",
  paidSubtitle = "Your payment was completed successfully",
  amountLabel = "Amount Paid",
  amountValue = "7.150 د.ك",
  detailsTitle = "Receipt Details",
  details = defaultDetails,
  orderTitle = "Your Order",
  orderItems = defaultOrderItems,
  subtotalLabel = "Subtotal",
  subtotalValue = "7.150 د.ك",
  totalLabel = "Total Paid",
  totalValue = "7.150 د.ك",
  secureText = "Secure payment via Enabill",
  addsInfo = [],
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => t(key, { defaultValue: fallback });
  const appDispatch = useContext(DispatchContext);
  const appState = useContext(StateContext);
  const invoice = invoiceDetail || {};
  const invoiceable = invoice?.invoiceable || {};
  const currencyCode = normalizeCurrencyCode(
    storeInfo?.currencyCode || storeInfo?.currency || "KWD"
  );
  const isQuickInvoice = invoice?.type === "QuickInvoice";
  const hasDelivery = invoiceable?.service_type == "Delivery" ? true : false;
  const [orderStatus, setOrderStatus] = useState("Completed");

  const formatAmount = (value) => {
    const raw = formatMoney(value, currencyCode, appState?.langValue);
    return localizeWesternDigitsForLang(raw, appState?.langValue);
  };

  const statusIconVariant = useMemo(() => {
    if (eventOrder || invoice?.status === "Paid" || invoice?.status === "Direct") return "Paid";
    if (invoice?.status === "Pending") return "Pending";
    if (invoice?.status === "Processing") return "Processing";
    if (invoice?.status === "Failed") return "Failed";
    if (invoice?.status === "Cancelled") return "Cancelled";
    return null;
  }, [eventOrder, invoice?.status]);

  const statusText = useMemo(() => {
    if (eventOrder || invoice?.status === "Paid" || invoice?.status === "Direct") {
      return {
        title: t(`Paid`),
        subtitle: t(`Your payment is completed successfully`),
      };
    }
    if (invoice?.status === "Pending") {
      return {
        title: t(`Pending`),
        subtitle: t(`Your payment is waiting for confirmation`),
      };
    }
    if (invoice?.status === "Processing") {
      return {
        title: t(`Processing`),
        subtitle: t(`Your payment is being processed`),
      };
    }
    if (invoice?.status === "Failed") {
      return {
        title: t(`Failed`),
        subtitle: t(`Your payment could not be completed`),
      };
    }
    if (invoice?.status === "Cancelled") {
      return {
        title: t(`Cancelled`),
        subtitle: t(`Your payment is cancelled`),
      };
    }
    return { title: paidTitle, subtitle: paidSubtitle };
  }, [eventOrder, invoice?.status, paidTitle, paidSubtitle, t]);

  const titleStatusClass = useMemo(() => {
    if (eventOrder || invoice?.status === "Paid" || invoice?.status === "Direct") return "invoice-design-paid-title--success";
    if (invoice?.status === "Pending" || invoice?.status === "Processing") return "invoice-design-paid-title--warning";
    if (invoice?.status === "Failed" || invoice?.status === "Cancelled") return "invoice-design-paid-title--danger";
    return "invoice-design-paid-title--danger";
  }, [eventOrder, invoice?.status]);

  const getDateAndTime = (invoice) => {
    let dateVal = null;
    let timeVal = null;
    if (invoice?.is_paid) {
      dateVal = invoice?.paid?.date || invoice?.transaction?.captured?.date;
      timeVal = invoice?.paid?.time || invoice?.transaction?.captured?.time;
    } else if (invoice?.failed) {
      dateVal = invoice?.failed?.date;
      timeVal = invoice?.failed?.time;
    } else {
      dateVal = invoice?.transaction?.captured?.date;
      timeVal = invoice?.transaction?.captured?.time;
    }

    return { dateVal, timeVal };
  }
  const computedDetails = useMemo(() => {
    let paymentMethodLabel = "-";
    if (invoice?.status === "Direct") {
      const pm = invoice?.paymentMethod || { name: { en: "In Store", ar: "في المتجر" } };
      paymentMethodLabel = get_lang(pm?.name, appState?.langValue) || "-";
    } else if (invoice?.is_partial) {
      const pm = invoice?.paymentMethod || { name: { en: "Split", ar: "تقسيم" } };
      paymentMethodLabel = get_lang(pm?.name, appState?.langValue) || "-";
    } else {
      const source = invoice?.transaction?.payment_source;
      if (typeof source === "string" && source.trim()) {
        paymentMethodLabel = source;
      } else if (source && typeof source === "object") {
        paymentMethodLabel = get_lang(source?.name, appState?.langValue) || "-";
      } else if (invoice?.paymentMethod) {
        paymentMethodLabel =
          get_lang(invoice.paymentMethod?.name, appState?.langValue) ||
          (typeof invoice.paymentMethod === "string" ? invoice.paymentMethod : "-");
      }
    }

    const storeName =
      get_lang(invoice?.business_name, appState?.langValue) ||
      get_lang(invoice?.business?.name, appState?.langValue) ||
      storeInfo?.business_name ||
      "-";

    const { dateVal, timeVal } = getDateAndTime(invoice);
    const rows = [
      receiptDetailRow(t(`Invoice No`), `#${invoice?.id || "-"}`),
      receiptDetailRow(t(`Payment Method`), paymentMethodLabel),
      receiptDetailRow(t(`Date`), dateVal),
      receiptDetailRow(t(`Time`), timeVal),
      receiptDetailRow(t(`Customer`), invoice?.customer?.name || invoice?.name || "-"),
      receiptDetailRow(t(`Number`), invoice?.customer?.number || invoice?.transaction?.number || "-"),
      receiptDetailRow(t(`Store Location`), "Kuwait"),
      receiptDetailRow(t(`Order Type`), invoiceable?.service_type || "-"),
      receiptDetailRow(t(`Store Name`), storeName),
      receiptDetailRow(t(`Table`),invoiceable?.service_type === "DineIn" ? invoiceable?.info?.table?.name : null)
                  ];

    // if (invoiceable?.service?.number != null) {
    //   rows.push(receiptDetailRow(t(`Service number`), String(invoiceable.service.number)));
    // }

    if (Number(invoiceable?.tip) > 0) {
      rows.push(
        receiptDetailRow(t(`Tip`), formatMoney(invoiceable.tip, currencyCode, appState?.langValue)),
      );
    }

    if (Number(invoiceable?.delivery_charges) > 0) {
      rows.push(
        receiptDetailRow(
          t(`Delivery Charges`),
          formatMoney(invoiceable.delivery_charges, currencyCode, appState?.langValue),
        ),
      );
    }

    if (invoiceable?.campaigns?.length > 0 && invoiceable.campaigns[0]) {
      const c0 = invoiceable.campaigns[0];
      const campName = get_lang(c0?.campaign?.campaign?.name, appState?.langValue);
      rows.push(
        receiptDetailRow(
          campName ? `${campName} (${t(`campaign Discount`)})` : tr("invoice.campaign-discount", "campaign Discount"),
          c0?.discount != null ? String(c0.discount) : "-",
        ),
      );
    }

    if (Number(invoiceable?.discounted_amount ?? invoiceable?.eventDiscount?.amount) > 0) {
      const discountVal = invoiceable?.discounted_amount ?? invoiceable?.eventDiscount?.amount;
      const discountLabel = invoiceable?.eventDiscount
        ? t(`Event Discount`)
        : t(`Discount`);
      rows.push(
        receiptDetailRow(discountLabel, formatMoney(discountVal, currencyCode, appState?.langValue)),
      );
    }

    if (invoiceable?.service_type === "DineIn" && Number(invoiceable?.total_amount) > 0) {
      rows.push(
        receiptDetailRow(
          t(`Total Amount`),
          formatMoney(invoiceable.total_amount, currencyCode, appState?.langValue),
        ),
      );
    }

    if (invoiceable?.discounted_card != null && Number(invoiceable?.discounted_card?.discount?.amount) > 0) {
      const dc = invoiceable.discounted_card?.discount;
      const percent = dc?.percent;
      const fixed = dc?.fixed;
      let discText = "";
      if (percent > 0 && fixed > 0) {
        discText = `${parseFloat(percent)}% + ${formatMoney(fixed, currencyCode, appState?.langValue)}`;
      } else if (percent > 0) {
        discText = `${parseFloat(percent)}%`;
      } else if (fixed > 0) {
        discText = formatMoney(fixed, currencyCode, appState?.langValue);
      }
      rows.push(receiptDetailRow(t(`Boubyan Discount`), discText));
    }

    return rows
      .filter((row) => row.show)
      .map(({ label, value }) => ({
        label: localizeWesternDigitsForLang(label, appState?.langValue),
        value: localizeWesternDigitsForLang(value, appState?.langValue),
      }));
  }, [appState?.langValue, invoice, invoiceable, storeInfo?.business_name, currencyCode, t]);
  const computedOrderLines = useMemo(() => {
    const lines = [];

    (invoiceable?.orders || []).forEach((order, i) => {
      const addonRows = (order?.addons || []).map((addon, j) => {
        const addName = addonLineName(addon, appState?.langValue);
        const qty = addon?.quantity ? `${addon.quantity}× ` : "";
        const total = Number(addon?.total);
        const priceText = total > 0 ? formatAmount(total) : t(`free`);
        return {
          id: `order-${i}-addon-${j}`,
          name: localizeWesternDigitsForLang(`${qty}${addName}`, appState?.langValue),
          quantityText: t(`Add-on`),
          price: priceText,
        };
      });
      const personalizationRows = (order?.personalizations || []).map((p, j) => {
        const pName = get_lang(p?.product_personalization?.name, appState?.langValue) || "-";
        const qty = p?.quantity > 1 ? `${p.quantity}× ` : "";
        const total = Number(p?.total ?? p?.price);
        return {
          id: `order-${i}-pers-${j}`,
          name: localizeWesternDigitsForLang(`${qty}${pName}`, appState?.langValue),
          text: p?.text || "",
          quantityText: t(`Personalization`),
          price: total > 0 ? formatAmount(total) : t(`free`),
        };
      });
      lines.push({
        id: `order-${i}`,
        kind: "product",
        name: get_lang(order?.product?.name, appState?.langValue) || "-",
        quantityText: localizeWesternDigitsForLang(
          `${order?.quantity || 1}× ${t(`item`)}`,
          appState?.langValue,
        ),
        price: order?.amount != null ? formatAmount(order.amount) : "",
        addons: addonRows,
        personalizations: personalizationRows,
      });
    });

    (invoiceable?.combos || []).forEach((combo, i) => {
      lines.push({
        id: `combo-${i}`,
        kind: "combo",
        name: get_lang(combo?.combo?.name, appState?.langValue) || t(`Combo`),
        quantityText: localizeWesternDigitsForLang(`1 ${t(`combo`)}`, appState?.langValue),
        price: combo?.amount > 0 ? formatAmount(combo?.amount) : t(`free`),
      });
    });

    return lines;
  }, [appState?.langValue, invoiceable?.combos, invoiceable?.orders, t]);

  const [openAddonGroups, setOpenAddonGroups] = useState(() => ({}));
  const toggleAddonGroup = useCallback((lineId) => {
    setOpenAddonGroups((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
  }, []);

  const subTotal = Number(
    invoiceable?.sub_total ?? invoiceable?.order_total ?? invoiceable?.total_amount ?? 0,
  );
  const tipAmount = Number(invoiceable?.tip ?? 0);
  const deliveryCharges = Number(invoiceable?.delivery_charges ?? 0);
  const extraCharges = Number(invoiceable?.total_charges ?? 0);
  const chargeItems = Array.isArray(invoiceable?.charges) ? invoiceable.charges : [];
  const discountAmount = Number(
    invoiceable?.discounted_amount ?? invoiceable?.eventDiscount?.amount ?? 0,
  );
  const hasEventDiscount = Boolean(invoiceable?.eventDiscount);
  const payableAmount = Number(
    amount ??
      invoiceable?.amount ??
      (Number(invoice?.amount) > 0 ? invoice.amount : null) ??
      invoiceable?.total_amount ??
      0,
  );

  const finalAmountValue = invoiceDetail
    ? formatAmount(payableAmount)
    : localizeWesternDigitsForLang(amountValue, appState?.langValue);

  const finalDetails = useMemo(() => {
    if (invoiceDetail) return computedDetails;
    return details.map((item) => ({
      ...item,
      label: localizeWesternDigitsForLang(item.label, appState?.langValue),
      value: localizeWesternDigitsForLang(item.value, appState?.langValue),
    }));
  }, [invoiceDetail, computedDetails, details, appState?.langValue]);

  const showOrderSection = invoiceDetail && !isQuickInvoice;

  const finalOrderLines = useMemo(() => {
    if (invoiceDetail) return computedOrderLines;
    return orderItems.map((item) => ({
      ...item,
      name: localizeWesternDigitsForLang(item.name, appState?.langValue),
      quantityText: localizeWesternDigitsForLang(item.quantityText, appState?.langValue),
      price: localizeWesternDigitsForLang(item.price, appState?.langValue),
    }));
  }, [invoiceDetail, computedOrderLines, orderItems, appState?.langValue]);

  const finalSubtotal = invoiceDetail
    ? formatAmount(subTotal)
    : localizeWesternDigitsForLang(subtotalValue, appState?.langValue);
  const finalTotal = invoiceDetail
    ? formatAmount(payableAmount)
    : localizeWesternDigitsForLang(totalValue, appState?.langValue);
  const logo = invoice?.business?.logo || storeInfo?.logo || null;

  const amountIsError = invoice?.status === "Cancelled" || invoice?.status === "Failed";
  const showTryAgain =
    typeof handleTryAgain === "function" && (invoice?.status === "Failed" || invoice?.status === "Pending");

  const heroAmountLabel =
    invoice?.status === "Paid" || invoice?.status === "Direct" || eventOrder
      ? t(`Amount Paid`)
      : t(`Amount`);
  return (
    <div className="invoice-design-page">
      <div className="invoice-design-card">
        <InvoiceSharedHeader
          languageLabel={languageLabel}
          onLanguageToggle={onLanguageToggle}
          onPrimaryClick={onLoginClick}
          primaryType="icon"
          primaryLabel={t(`Login`)}
          onShareClick={onShareClick}
          onLogoutClick={() => appDispatch({ type: "logout" })}
          />

        <div className="d-flex align-items-center justify-content-center">
          {
          storeInfo?.ratings?.includes(vendorsConfig.SLUG) &&
            invoice.is_paid &&
            appState.reviewModal && 
            (
              <ReviewModalv2
                storeInfo={storeInfo}
                invoiceDetail={invoice}
              />
            )}
        </div>

        <div className="invoice-design-hero">
          <div className="invoice-design-logo-wrap">
            {logo ? (
              <img src={logo} alt="Store logo" className="invoice-design-logo-placeholder" />
            ) : (
              <div className="invoice-design-logo-placeholder" />
            )}
          </div>

          <div className="invoice-design-status-row" style={{ direction: 'ltr' }}>
            {statusIconVariant ? (
              <div className="invoice-design-status-icon-wrap" >
                <InvoiceStatusIconPng variant={statusIconVariant} />
              </div>
            ) : null}
            <h1 className={`invoice-design-paid-title  ${titleStatusClass}`} >{statusText.title}</h1>
          </div>
          <div className="invoice-design-paid-sub">{statusText.subtitle}</div>
          <div className="invoice-design-amount-label">{heroAmountLabel}</div>
          <div
            className={`invoice-design-amount-value ${amountIsError ? "invoice-design-amount-value--error" : ""}`}
          >
            {finalAmountValue}
          </div>
        </div>

        {showTryAgain ? (
          <div className="invoice-design-try-again">
            {/* <Button className="invoice-design-try-again-btn bg-primary text-white w-100" onClick={handleTryAgain}>
              {tr("invoice.try-again", "Try Again")}
            </Button> */}
            <StandardBtn defaultText={t(`Try Again`)} onClick={handleTryAgain} />
          </div>
        ) : null}

        {addsInfo != null && addsInfo.length > 0 && (
          <span
            className={`${addsInfo.length > 1
                ? ``
                : `d-flex align-items-center justify-content-center`
              }`}
          >
            {" "}
            <AddsSlider addsInfo={addsInfo} />{" "}
          </span>
        )}

        <div className="invoice-design-block">
          <div className="invoice-design-block-title">
            {t(`Invoice details`)}
          </div>
          <div className="invoice-design-info-grid">
            {finalDetails.filter((item) => item.show !== false).map((item) => (
              <div className="invoice-design-info-item" key={`${item.label}-${item.value}`}>
                <div className="invoice-design-info-label">{item.label}</div>
                <div className="invoice-design-info-value">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {showOrderSection ? (
          <div className="invoice-design-block">
            <div className="invoice-design-block-title">{t(`Your Order`)}</div>
            <div className="invoice-design-order-list">
              {finalOrderLines.map((item) => {
                const lineKey = item.id || `${item.name}-${item.price}`;
                const addonList = item.kind === "product" && Array.isArray(item.addons) ? item.addons : [];
                const personalizationList =
                  item.kind === "product" && Array.isArray(item.personalizations)
                    ? item.personalizations
                    : [];
                const hasAddonAccordion = addonList.length > 0 || personalizationList.length > 0;
                const addonsOpen = Boolean(openAddonGroups[lineKey]);

                if (hasAddonAccordion) {
                  const hintParts = [];
                  if (addonList.length > 0) {
                    hintParts.push(
                      `${addonList.length} ${addonList.length === 1 ? t(`Add-on`) : t(`Add-ons`)}`,
                    );
                  }
                  if (personalizationList.length > 0) {
                    hintParts.push(
                      `${personalizationList.length} ${
                        personalizationList.length === 1
                          ? t(`Personalization`)
                          : t(`Personalizations`)
                      }`,
                    );
                  }
                  const extrasHint = localizeWesternDigitsForLang(
                    hintParts.join(" · "),
                    appState?.langValue,
                  );
                  return (
                    <div className="invoice-design-order-group" key={lineKey}>
                      <button
                        type="button"
                        className="invoice-design-order-accordion-trigger"
                        aria-expanded={addonsOpen}
                        aria-controls={`invoice-design-addons-${lineKey}`}
                        id={`invoice-design-order-head-${lineKey}`}
                        onClick={() => toggleAddonGroup(lineKey)}
                      >
                        <div className="invoice-design-order-main">
                          <div className="invoice-design-order-name">{item.name}</div>
                          <div className="invoice-design-order-note">
                            {item.quantityText}
                            <span className="invoice-design-order-addons-hint"> · {extrasHint}</span>
                          </div>
                        </div>
                        <div className="invoice-design-order-accordion-end">
                          <div className="invoice-design-order-price">{item.price}</div>
                          <ExpandMoreIcon
                            className={`invoice-design-order-chevron ${addonsOpen ? "invoice-design-order-chevron--open" : ""}`}
                            aria-hidden
                          />
                        </div>
                      </button>
                      <div
                        className={`invoice-design-order-addons-panel ${addonsOpen ? "invoice-design-order-addons-panel--open" : ""}`}
                        id={`invoice-design-addons-${lineKey}`}
                        role="region"
                        aria-labelledby={`invoice-design-order-head-${lineKey}`}
                        aria-hidden={!addonsOpen}
                      >
                        <div className="invoice-design-order-addons-panel-inner">
                          {addonList.map((addon) => (
                            <div className="invoice-design-order-item invoice-design-order-item--addon" key={addon.id}>
                              <div className="invoice-design-order-main">
                                <div className="invoice-design-order-name">{addon.name}</div>
                                <div className="invoice-design-order-note">{addon.quantityText}</div>
                              </div>
                              <div className="invoice-design-order-price">{addon.price}</div>
                            </div>
                          ))}
                          {personalizationList.map((pers) => (
                            <div
                              className="invoice-design-order-item invoice-design-order-item--addon invoice-design-order-item--personalization"
                              key={pers.id}
                            >
                              <div className="invoice-design-order-main">
                                <div className="invoice-design-order-name">{pers.name}</div>
                                {pers.text ? (
                                  <div className="invoice-design-order-note invoice-design-order-personalization-text">
                                    {localizeWesternDigitsForLang(pers.text, appState?.langValue)}
                                  </div>
                                ) : (
                                  <div className="invoice-design-order-note">{pers.quantityText}</div>
                                )}
                              </div>
                              <div className="invoice-design-order-price">{pers.price}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="invoice-design-order-item" key={lineKey}>
                    <div className="invoice-design-order-main">
                      <div className="invoice-design-order-name">{item.name}</div>
                      <div className="invoice-design-order-note">{item.quantityText}</div>
                    </div>
                    <div className="invoice-design-order-price">{item.price}</div>
                  </div>
                );
              })}
            </div>

            <div className="invoice-design-summary">
              <div className="invoice-design-summary-row">
                <span>{t(`Subtotal`)}</span>
                <strong>{finalSubtotal}</strong>
              </div>
              {invoiceDetail && tipAmount > 0 ? (
                <div className="invoice-design-summary-row">
                  <span>{t(`Tip`)}</span>
                  <strong>{formatAmount(tipAmount)}</strong>
                </div>
              ) : null}
              {invoiceDetail && deliveryCharges > 0 ? (
                <div className="invoice-design-summary-row">
                  <span>{t(`Delivery Charges`)}</span>
                  <strong>{formatAmount(deliveryCharges)}</strong>
                </div>
              ) : null}
              {invoiceDetail &&
                chargeItems.map((charge, index) => {
                  const chargeName =
                    get_lang(charge?.name, appState?.langValue) ||
                    charge?.title ||
                    t(`Charges`);
                  const chargeValue = Number(charge?.amount ?? charge?.total ?? 0);
                  if (!(chargeValue > 0)) return null;
                  return (
                    <div className="invoice-design-summary-row" key={`charge-${index}`}>
                      <span>{chargeName}</span>
                      <strong>{formatAmount(chargeValue)}</strong>
                    </div>
                  );
                })}
              {invoiceDetail && extraCharges > 0 && chargeItems.length === 0 ? (
                <div className="invoice-design-summary-row">
                  <span>{t(`Charges`)}</span>
                  <strong>{formatAmount(extraCharges)}</strong>
                </div>
              ) : null}
              {invoiceDetail && discountAmount > 0 ? (
                <div className="invoice-design-summary-row">
                  <span>{hasEventDiscount ? t(`Event Discount`) : t(`Discount`)}</span>
                  <strong>-{formatAmount(discountAmount)}</strong>
                </div>
              ) : null}
              <div className="invoice-design-summary-row total">
                <span>
                  {invoice?.status === "Paid" || invoice?.status === "Direct" || eventOrder
                    ? t(`Total Paid`)
                    : t(`Total`)}
                </span>
                <strong>{finalTotal}</strong>
              </div>
            </div>
            {invoice?.is_partial ? (
              <PartialPaymentSummary invoiceDetails={invoice} />
            ) : null}
          </div>
        ) : null}

        <InvoiceSharedFooter text={tr("invoice.ripos-secure-payment", secureText)} logoType='app-logo' />
      </div>
    </div>
  );
}

export default InvoiceDesign;
